import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';
import type { Bible, CanonicalBook, OpenBible } from '../types';

const OPEN_BIBLES_KEY = 'byble_open_bibles';
const MAX_OPEN_BIBLES = 3;

interface BibleContextValue {
  openBibles: OpenBible[];
  addOpenBible: (defaultBibleId: number, defaultBookId: number) => void;
  removeOpenBible: (openBibleId: number) => void;
  updateOpenBible: (openBibleId: number, updates: Partial<OpenBible>) => void;
  validateOpenBibles: (bibles: Bible[], books: CanonicalBook[]) => void;
}

const BibleContext = createContext<BibleContextValue | null>(null);

function loadSavedBibles(): OpenBible[] {
  const saved = localStorage.getItem(OPEN_BIBLES_KEY);
  if (!saved) return [];
  try {
    return JSON.parse(saved);
  } catch {
    return [];
  }
}

function persistBibles(bibles: OpenBible[]) {
  localStorage.setItem(OPEN_BIBLES_KEY, JSON.stringify(bibles));
}

export function BibleProvider({ children }: { children: ReactNode }) {
  const [openBibles, setOpenBibles] = useState<OpenBible[]>(loadSavedBibles);
  const lastValidatedKey = useRef('');

  const addOpenBible = useCallback((defaultBibleId: number, defaultBookId: number) => {
    setOpenBibles(prev => {
      if (prev.length >= MAX_OPEN_BIBLES) return prev;
      const newBible: OpenBible = {
        id: Date.now(),
        bibleId: defaultBibleId,
        canonicalBookId: defaultBookId,
        selectedChapter: 1,
      };
      const updated = [...prev, newBible];
      persistBibles(updated);
      return updated;
    });
  }, []);

  const removeOpenBible = useCallback((openBibleId: number) => {
    setOpenBibles(prev => {
      if (prev.length <= 1) return prev;
      const updated = prev.filter(ob => ob.id !== openBibleId);
      persistBibles(updated);
      return updated;
    });
  }, []);

  const updateOpenBible = useCallback((openBibleId: number, updates: Partial<OpenBible>) => {
    setOpenBibles(prev => {
      const updated = prev.map(ob =>
        ob.id === openBibleId ? { ...ob, ...updates } : ob
      );
      persistBibles(updated);
      return updated;
    });
  }, []);

  const validateOpenBibles = useCallback((bibles: Bible[], books: CanonicalBook[]) => {
    const key = bibles.map(b => b.id).join(',') + '|' + books.map(b => b.id).join(',');
    if (key === lastValidatedKey.current) return;
    lastValidatedKey.current = key;

    setOpenBibles(prev => {
      const valid = prev.filter(ob =>
        bibles.some(b => b.id === ob.bibleId) &&
        books.some(b => b.id === ob.canonicalBookId)
      );
      if (valid.length === prev.length) return prev;
      persistBibles(valid);
      return valid;
    });
  }, []);

  const value: BibleContextValue = {
    openBibles,
    addOpenBible,
    removeOpenBible,
    updateOpenBible,
    validateOpenBibles,
  };

  return (
    <BibleContext.Provider value={value}>
      {children}
    </BibleContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBible() {
  const context = useContext(BibleContext);
  if (!context) {
    throw new Error('useBible must be used within a BibleProvider');
  }
  return context;
}

export default BibleContext;
